import React, { useEffect, useRef, useState } from 'react';
import { TrialResult } from '../../types';

interface ExampleTrialsProps {
  trialsData: TrialResult[];
  peopleNames: string[];
  maxTrials?: number;
}

/**
 * Component for displaying example trial tables
 */
export const ExampleTrials: React.FC<ExampleTrialsProps> = ({
  trialsData,
  peopleNames,
  maxTrials = 4
}) => {
  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleTables, setVisibleTables] = useState<Set<number>>(new Set());

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    containerRefs.current.forEach((container, index) => {
      if (container) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              setVisibleTables(prev => {
                const newSet = new Set(prev);
                if (entry.isIntersecting) {
                  newSet.add(index);
                } else {
                  newSet.delete(index);
                }
                return newSet;
              });
            });
          },
          {
            threshold: 0,
            rootMargin: '50px 0px'
          }
        );

        observer.observe(container);
        observers.push(observer);
      }
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [trialsData]);
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatPercent = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatAccountingCurrency = (value: number): string => {
    const options = {
      style: 'currency' as const,
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    
    if (value < 0) {
      return `($${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })})`;
    } else {
      return value.toLocaleString('en-US', options);
    }
  };

  const getDisplayStatus = (status: string): string => {
    return status === 'Deceased' ? '' : status;
  };

  if (!trialsData || trialsData.length === 0) {
    return (
      <div className="example-trials">
        <h3>Example Trials</h3>
        <p>No trial data available.</p>
      </div>
    );
  }

  const displayTrials = trialsData.slice(0, maxTrials);

  return (
    <div className="example-trials">
      <h3>Example Trials</h3>
      <div className="trials-container">
        {displayTrials.map((trialResult, index) => {
          const trialHistory = trialResult.history;
          const finalBalance = trialResult.finalBalance;
          const wasOverdrawn = trialResult.wasOverdrawn;
          
          // Determine trial status: successful (never overdrawn), overdrawn (was overdrawn but positive final), or failed (negative final)
          let trialStatus: 'successful' | 'overdrawn' | 'failed';
          if (finalBalance <= 0) {
            trialStatus = 'failed';
          } else if (wasOverdrawn) {
            trialStatus = 'overdrawn';
          } else {
            trialStatus = 'successful';
          }
          return (
          <div 
            key={index} 
            className={`trial-table-container ${visibleTables.has(index) ? 'table-visible' : 'table-hidden'}`}
            ref={(el) => {
              containerRefs.current[index] = el;
            }}
          >
            <table className="trial-table">
              <caption className={`trial-${trialStatus}`}>
                Trial {index + 1}
              </caption>
              
              <thead>
                {/* First header row with spanning headers */}
                <tr>
                  <th rowSpan={2} className="col-year">Year</th>
                  <th rowSpan={2} className="col-inflation">Inflation</th>
                  
                  {peopleNames.map((name) => (
                    <th key={name} colSpan={5} className="col-person-span">
                      {name}
                    </th>
                  ))}
                  
                  <th colSpan={8} className="col-shared-span">
                    Shared Account
                  </th>
                </tr>
                
                {/* Second header row with specific metrics */}
                <tr>
                  {peopleNames.map((name) => (
                    <React.Fragment key={name}>
                      <th className="col-age">Age</th>
                      <th className="col-status">Status</th>
                      <th className="col-income">Income</th>
                      <th className="col-deposit">Invest Deposit</th>
                      <th className="col-ss-benefit">SS Benefit</th>
                    </React.Fragment>
                  ))}
                  
                  <th className="col-shared-start">Start Bal</th>
                  <th className="col-shared-return">Return</th>
                  <th className="col-shared-deposit">Deposit</th>
                  <th className="col-shared-need">Need</th>
                  <th className="col-shared-less-ss">Less SS</th>
                  <th className="col-shared-less-income">Less Inc.</th>
                  <th className="col-shared-withdrawal">Withdrawal</th>
                  <th className="col-shared-end">End Bal</th>
                </tr>
              </thead>
              
              <tbody>
                {trialHistory.map((yearData, yearIndex) => (
                  <tr key={yearIndex}>
                    {/* Year & Inflation */}
                    <td className="col-year">{yearData.year}</td>
                    <td className="col-inflation">{formatPercent(yearData.inflationRate)}</td>
                    
                    {/* Per-person data */}
                    {peopleNames.map((name) => {
                      const personData = yearData.persons.find(p => p.name === name);
                      return (
                        <React.Fragment key={name}>
                          <td className="col-age">
                            {personData ? personData.age : ''}
                          </td>
                          <td className="col-status">
                            {personData ? getDisplayStatus(personData.status) : ''}
                          </td>
                          <td className="col-income">
                            {personData ? formatCurrency(personData.totalIncome || 0) : ''}
                          </td>
                          <td className="col-deposit">
                            {personData ? formatCurrency(personData.investmentDeposit || 0) : ''}
                          </td>
                          <td className="col-ss-benefit">
                            {personData ? formatCurrency(personData.socialSecurityBenefit || 0) : ''}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Shared account data */}
                    <td className="col-shared-start">
                      {formatCurrency(yearData.sharedAccount.startingBalance)}
                    </td>
                    <td className="col-shared-return">
                      {formatPercent(yearData.sharedAccount.rateOfReturn)}
                    </td>
                    <td className="col-shared-deposit">
                      {formatCurrency(yearData.sharedAccount.totalDeposit)}
                    </td>
                    <td className="col-shared-need">
                      {formatAccountingCurrency(-yearData.sharedAccount.neededIncome)}
                    </td>
                    <td className="col-shared-less-ss">
                      {formatCurrency(yearData.sharedAccount.totalSSBenefit)}
                    </td>
                    <td className="col-shared-less-income">
                      {formatCurrency(yearData.sharedAccount.totalNonRetiredIncome)}
                    </td>
                    <td className="col-shared-withdrawal">
                      {formatAccountingCurrency(-yearData.sharedAccount.withdrawal)}
                    </td>
                    <td className={`col-shared-end ${yearData.sharedAccount.isOverdrawn ? 'overdrawn' : ''}`}>
                      {formatAccountingCurrency(yearData.sharedAccount.endingBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          );
        })}
      </div>
    </div>
  );
};
